<template>
  <div class="post-detail-container">
    <div v-if="loading" class="loading">加载中...</div>
    <div v-else-if="postDetail" class="post-detail-content">
      <!-- 帖子基本信息 -->
      <div class="post-header">
        <div class="user-info">
          <div class="user-info-content">
            <img :src="postDetail.userAvatar" alt="用户头像" class="avatar" />
            <div class="user-text">
              <h3>{{ postDetail.name }}</h3>
              <p class="create-time">{{ formatDate(postDetail.createTime) }}</p>
              <!-- 将统计数据移到这里 -->
              <div class="post-stats">
                <span><i class="icon-view"></i> 浏览: {{ postDetail.viewCount }}</span>
                <span><i class="icon-like"></i> 点赞: {{ postDetail.likeCount }}</span>
                <span><i class="icon-favorite"></i> 收藏: {{ postDetail.favoriteCount }}</span>
                <span><i class="icon-comment"></i> 评论: {{ postDetail.commentCount }}</span>
              </div>
            </div>
          </div>
          <!-- 关注按钮 - 只有当当前用户不是帖子发布者时才显示 -->
          <button 
            v-if="postDetail && computedUserId !== postDetail.userId"
            @click.stop="toggleFollowDetail(postDetail!)"
            :class="['follow-btn', { followed: postDetail?.isFollowed === 1 }]"
          >
            <i :class="postDetail?.isFollowed === 1 ? 'icon-followed' : 'icon-follow'"></i>
            {{ postDetail?.isFollowed === 1 ? '已关注' : '关注' }}
          </button>
          
          <!-- 举报按钮 - 只有当当前用户不是帖子发布者时才显示 -->
          <button 
            v-if="postDetail && computedUserId !== postDetail.userId"
            class="report-btn"
            @click="openReportDialog"
          >
            <i class="icon-report">举报</i>
          </button>
        </div>
      </div>
      
      <!-- 编辑帖子模态框 -->
      <EditPostModal
        v-model="editDialogVisible"
        :post-id="editingPostId"
        @save-success="onEditSuccess"
      />
      
      <div class="post-content">
        <h2 class="post-title">{{ postDetail.title }}</h2>
        <!-- 使用v-html渲染Markdown内容 -->
        <div class="post-body" v-html="renderedContent"></div>
        <!-- 媒体列表展示 -->
        <div v-if="postDetail.mediaList && postDetail.mediaList.length > 0" class="media-list">
          <!-- 图片网格 -->
          <div v-if="hasImages" class="image-grid">
            <div 
              v-for="media in imageMediaList" 
              :key="media.id"
              class="media-item"
            >
              <div class="image-wrapper">
                <img 
                  :src="`${apiBaseUrl}${media.mediaUrl}`" 
                  :alt="`Media ${media.id}`"
                  @click="openImageViewer(getImageIndex(media.id))"
                  style="cursor: pointer;"
                />
              </div>
            </div>
          </div>
          
          <!-- 视频列表（每个视频独占一行） -->
          <div 
            v-for="media in videoMediaList" 
            :key="`video-${media.id}`"
            class="video-full-width"
          >
            <div class="video-wrapper">
              <video 
                :src="`${apiBaseUrl}${media.mediaUrl}`" 
                :poster="media.thumbnailUrl ? `${apiBaseUrl}${media.thumbnailUrl}` : ''"
                controls
                preload="metadata"
              >
                您的浏览器不支持视频播放
              </video>
              <div v-if="media.duration" class="video-duration">
                {{ formatDuration(media.duration) }}
              </div>
            </div>
          </div>
        </div>

      </div>
      
      <!-- 图片预览模态框 -->
      <div v-if="showImageViewer" class="image-viewer-overlay" @click="closeImageViewer">
        <div class="image-viewer-content" @click.stop>
          <button class="nav-btn prev-btn" @click.stop="prevImage" v-if="imageMediaList.length > 1">&#10094;</button>
          <div class="image-display">
            <img :src="`${apiBaseUrl}${currentImage?.mediaUrl}`" :alt="`Image ${currentImage?.id}`" />
            <div class="image-counter">{{ currentIndex + 1 }} / {{ imageMediaList.length }}</div>
          </div>
          <button class="nav-btn next-btn" @click.stop="nextImage" v-if="imageMediaList.length > 1">&#10095;</button>
          <button class="close-btn" @click="closeImageViewer">&#10005;</button>
        </div>
      </div>

      <!-- 操作按钮 -->
      <div class="post-actions">
        <button 
          @click="toggleLike" 
          :class="['action-btn', { liked: postDetail.isLiked === 1 }]">
          <i :class="postDetail.isLiked === 1 ? 'icon-liked' : 'icon-like'"></i>
          {{ postDetail.isLiked === 1 ? '已点赞' : '点赞' }} ({{ postDetail.likeCount }})
        </button>
        <button 
          class="action-btn comment-btn"
          @click="showCommentInput = !showCommentInput"
        >
          <i class="icon-comment"></i> 评论
        </button>
        <button 
          @click="toggleFavorite" 
          :class="['action-btn', { favorited: postDetail.isFavorited === 1 }]">
          <i :class="postDetail.isFavorited === 1 ? 'icon-favorited' : 'icon-favorite'"></i>
          {{ postDetail.isFavorited === 1 ? '已收藏' : '收藏' }} ({{ postDetail.favoriteCount }})
        </button>
        
        <!-- 编辑/删除按钮，仅当帖子发布者是当前用户时显示 -->
        <div v-if="computedUserId === postDetail?.userId" class="user-actions">
          <button 
            class="action-btn edit-btn"
            @click="openEditModal"
          >
            <i class="icon-edit"></i> 编辑
          </button>
          <button 
            class="action-btn delete-btn"
            @click="confirmDeletePost"
          >
            <i class="icon-delete"></i> 删除
          </button>
        </div>
      </div>

      <!-- 主评论输入框 -->
      <div v-if="showCommentInput" class="main-comment-input">
        <textarea
          v-model="mainCommentContent"
          placeholder="写下你的评论..."
          class="comment-textarea"
          rows="4"
        ></textarea>
        <div class="comment-actions-footer">
          <button @click="cancelMainComment" class="cancel-btn">取消</button>
          <button @click="submitPostComment" class="submit-btn" :disabled="submittingComment">发送</button>
        </div>
      </div>

      <!-- 热门评论 -->
      <div v-if="postDetail.hotComment" class="hot-comment-section">
        <h3>热门评论</h3>
        <div class="comment-item">
          <img :src="postDetail.hotComment.userAvatar" alt="评论者头像" class="comment-avatar" />
          <div class="comment-content">
            <div class="comment-header">
              <strong>{{ postDetail.hotComment.name }}</strong>
              <span class="comment-time">{{ formatDate(postDetail.hotComment.createTime) }}</span>
            </div>
            <p>{{ postDetail.hotComment.content }}</p>
            <div class="comment-actions">
              <button class="comment-like-btn">
                <i class="icon-like"></i> {{ postDetail.hotComment.likeCount }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 所有评论 -->
      <div class="comments-section">
        <h3>全部评论 ({{ totalComments }})</h3>
        
        <div 
          v-for="comment in currentComments" 
          :key="comment.id" 
          class="comment-item"
        >
          <img :src="comment.userAvatar" alt="评论者头像" class="comment-avatar" />
          <div class="comment-content">
            <div class="comment-header">
              <strong>{{ comment.name }}</strong>
              <span class="comment-time">{{ formatDate(comment.createTime) }}</span>
            </div>
            <p>{{ comment.content }}</p>
            <div class="comment-actions">
              <!-- 展开回复按钮 -->
              <button 
                v-if="comment.childCommentCount && comment.childCommentCount > 0"
                class="show-replies-btn" 
                @click="toggleReplies(comment)"
              >
                <i class="icon-reply"></i>{{ getReplyButtonText(comment) }}
              </button>
              
              <!-- 评论按钮 -->
              <button 
                class="comment-reply-btn"
                @click="showReplyInput(comment.id)"
              >
                <i class="icon-reply"></i> 回复
              </button>
              
              <button 
                @click="toggleCommentLike(comment)"
                :class="['comment-like-btn', { liked: comment.isLiked === 1 }]">
                <i :class="comment.isLiked === 1 ? 'icon-comment-liked' : 'icon-comment-like'"></i> {{ comment.likeCount }}
              </button>
              
              <!-- 删除评论按钮 - 仅当评论是当前用户发布时显示 -->
              <button 
                v-if="computedUserId === comment.userId"
                @click.stop="deleteCurrentComment(comment)"
                class="comment-delete-btn"
              >
                <i class="icon-delete"></i> 删除
              </button>
              
              <!-- 举报评论按钮 - 仅当评论不是当前用户发布时显示 -->
              <button 
                v-if="computedUserId !== comment.userId"
                class="comment-report-btn"
                @click="openCommentReportDialog(comment.id)"
              >
                <i class="icon-report">举报</i>
              </button>
            </div>
            
            <!-- 回复输入框 -->
            <div v-if="showingReplyInput === comment.id" class="reply-input-box">
              <textarea
                v-model="replyContents[comment.id]"
                :placeholder="`回复 @${comment.name}...`"
                class="comment-textarea"
                rows="3"
              ></textarea>
              <div class="comment-actions-footer">
                <button @click="cancelReply" class="cancel-btn">取消</button>
                <button @click="submitReply(comment.id, comment.name)" class="submit-btn" :disabled="submittingReply">回复</button>
              </div>
            </div>
            
            <!-- 子评论列表组件 -->
            <div v-if="comment.showReplies" class="replies-container">
              <ChildCommentList 
                :postId="getPostId()!" 
                :parentId="comment.id" 
                @report-comment="handleReportEvent"
              />
            </div>
          </div>
        </div>
        
        <!-- 分页组件 -->
        <el-pagination
          v-if="totalComments > 0"
          @current-change="handlePageChange"
          :current-page="currentPage"
          :page-size="pageSize"
          layout="prev, pager, next"
          :total="totalComments"
          class="pagination-container"
          :disabled="commentsLoading"
        />
      </div>
    </div>
    <div v-else class="error">未找到帖子</div>
  </div>
  
  <!-- 举报对话框 -->
  <ReportModal ref="reportModalRef" />
  
  <!-- 编辑帖子模态框 -->
  <EditPostModal
    v-model="editDialogVisible"
    :post-id="editingPostId"
    @save-success="onEditSuccess"
  />
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, computed, watch, onUnmounted, defineProps } from 'vue';

// 定义组件 Props
const props = defineProps<{
  postId?: number;
}>();

import { useRoute, useRouter } from 'vue-router';
import { ElMessage, ElMessageBox, ElPagination } from 'element-plus';
import { useStore } from 'vuex';
// 修复导入 - 使用正确的函数名
import { getPostDetail, createComment, likePost, favoritePost, followUser, deleteComment, getCommentList, deletePost } from '@/user/api/post';
import { likeComment } from '@/user/api/interaction';
import { addReport } from '@/user/api/userApi';
import ReportModal from '@/user/views/report/ReportModal.vue';
import { formatDate } from '@/utils/date';
import ChildCommentList from '@/user/views/comment/ChildCommentList.vue';
import EditPostModal from './EditPostModal.vue';
import apiClient from '@/user/api/post';
import MarkdownIt from 'markdown-it';

// 创建MarkdownIt实例
const md = new MarkdownIt();

// 初始化router和store实例
const router = useRouter(); // 添加router实例
const store = useStore();

// 声明 reportModalRef
const reportModalRef = ref();

// 计算属性获取当前登录用户的ID
const computedUserId = computed(() => {
  const userInfo = store.state.userInfo;
  return userInfo?.id || null;
});

// 编辑帖子相关
const editingPostId = ref<number | null>(null);
const editDialogVisible = ref(false);

// 打开编辑模态框
const openEditModal = () => {
  if (postDetail.value) {
    // 设置编辑的帖子ID
    editingPostId.value = postDetail.value.id;
    editDialogVisible.value = true;
  }
};

// 编辑成功后的回调
const onEditSuccess = () => {
  editDialogVisible.value = false;
  editingPostId.value = null;
  // 重新加载帖子详情
  loadPostDetail();
};

// 确认删除帖子
const confirmDeletePost = async () => {
  try {
    await ElMessageBox.confirm(
      '确定要删除这个帖子吗？此操作不可撤销。',
      '删除确认',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }
    );
    
    if (postDetail.value) {
      // 调用删除帖子的API
      const response = await deletePost(postDetail.value.id);
      
      if (response.code === 1) {
        ElMessage.success('帖子删除成功');
        // 返回上一页
        router.go(-1);
      } else {
        ElMessage.error(response.msg || '删除帖子失败');
      }
    }
  } catch (error) {
    // 用户取消删除或发生错误
    if (error !== 'cancel') {
      console.error('删除帖子失败:', error);
      ElMessage.error('删除帖子失败');
    }
  }
};

// 响应式数据
const postDetail = ref<any /*PostDetail*/ | null>(null);  // 使用 any 类型注解避免 ESLint 报错
const comments = ref<Comment[]>([]); // 评论数据
const totalComments = ref(0); // 评论总数
const loading = ref(true);
const commentsLoading = ref(false); // 评论加载状态

// 计算属性渲染Markdown内容
const renderedContent = computed(() => {
  if (!postDetail.value?.content) return '';
  return md.render(postDetail.value.content);
});

// 分页相关数据
const currentPage = ref(1);
const pageSize = ref(5); // 每页显示5条评论

// 主评论相关数据
const showCommentInput = ref(false);
const mainCommentContent = ref('');
const submittingComment = ref(false);

// 回复相关数据
const showingReplyInput = ref<number | null>(null); // 记录当前显示回复输入框的评论ID
const replyContents = ref<{[key: number]: string}>({}); // 存储各个评论的回复内容
const submittingReply = ref(false); // 是否正在提交回复

// 从 props 或路由参数获取帖子ID
const getPostId = (): number | null => {
  // 优先使用 props 中的 postId，如果没有则尝试从路由参数获取
  if (props?.postId && props.postId > 0) {
    console.log('Using postId from props:', props.postId);
    return props.postId;
  }
  
  try {
    const route = useRoute();
    // 检查 route 和 route.params 是否存在
    if (route && route.params && route.params.postId !== undefined) {
      const routeId = Number(route.params.postId);
      if (!isNaN(routeId) && routeId > 0) {
        console.log('Using postId from route:', routeId);
        return routeId;
      }
    }
  } catch (error) {
    console.error('Error getting route params:', error);
  }
  
  console.error('Could not determine post ID from props or route');
  return null;
};

// 获取当前页的评论
const currentComments = computed(() => {
  return comments.value as ExtendedComment[];
});

// 区分图片和视频
const imageMediaList = computed(() => {
  if (!postDetail.value?.mediaList) return [];
  return postDetail.value.mediaList
    .filter(media => media.mediaType === 1)
    .sort((a, b) => {
      if (a.sortOrder !== null && b.sortOrder !== null) {
        return a.sortOrder - b.sortOrder;
      } else if (a.sortOrder !== null) {
        return -1;
      } else if (b.sortOrder !== null) {
        return 1;
      }
      return a.id - b.id;
    });
});

const videoMediaList = computed(() => {
  if (!postDetail.value?.mediaList) return [];
  return postDetail.value.mediaList
    .filter(media => media.mediaType === 2)
    .sort((a, b) => {
      if (a.sortOrder !== null && b.sortOrder !== null) {
        return a.sortOrder - b.sortOrder;
      } else if (a.sortOrder !== null) {
        return -1;
      } else if (b.sortOrder !== null) {
        return 1;
      }
      return a.id - b.id;
    });
});

const hasImages = computed(() => {
  return imageMediaList.value.length > 0;
});

const hasVideos = computed(() => {
  return videoMediaList.value.length > 0;
});

// 图片预览相关数据
const showImageViewer = ref(false);
const currentIndex = ref(0);

// 当前显示的图片
const currentImage = computed(() => {
  if (imageMediaList.value.length > 0 && currentIndex.value < imageMediaList.value.length) {
    return imageMediaList.value[currentIndex.value];
  }
  return null;
});

// 打开图片预览
const openImageViewer = (index: number) => {
  currentIndex.value = index;
  showImageViewer.value = true;
  // 阻止背景滚动
  document.body.style.overflow = 'hidden';
};

// 关闭图片预览
const closeImageViewer = () => {
  showImageViewer.value = false;
  // 恢复背景滚动
  document.body.style.overflow = '';
};

// 获取图片索引
const getImageIndex = (id: number) => {
  return imageMediaList.value.findIndex(img => img.id === id);
};

// 上一张图片
const prevImage = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--;
  } else {
    currentIndex.value = imageMediaList.value.length - 1; // 循环到最后一张
  }
};

// 下一张图片
const nextImage = () => {
  if (currentIndex.value < imageMediaList.value.length - 1) {
    currentIndex.value++;
  } else {
    currentIndex.value = 0; // 循环到第一张
  }
};

// 键盘事件支持
const handleKeyDown = (e: KeyboardEvent) => {
  if (!showImageViewer.value) return;
  
  if (e.key === 'Escape') {
    closeImageViewer();
  } else if (e.key === 'ArrowLeft') {
    prevImage();
  } else if (e.key === 'ArrowRight') {
    nextImage();
  }
};

// 监听键盘事件
onMounted(() => {
  window.addEventListener('keydown', handleKeyDown);
});

// 组件销毁时移除事件监听
onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyDown);
  // 确保在组件卸载时恢复滚动
  document.body.style.overflow = '';
});

// API基础URL
const apiBaseUrl = computed(() => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL;
  return (baseUrl ? baseUrl : '/api'); // 使用三元运算符替代类型断言
});

// 格式化视频时长（秒转为 MM:SS）
const formatDuration = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// 处理分页变化
const handlePageChange = async (page: number) => {
  console.log('Page changed to:', page);
  currentPage.value = page;
  await loadComments(page, pageSize.value);
};

// 扩展Comment类型，添加显示控制属性
type ExtendedComment = Comment & {
  showReplies?: boolean;
};

// 获取回复按钮的文本
const getReplyButtonText = (comment: ExtendedComment) => {
  // 使用API返回的childCommentCount字段
  const replyCount = comment.childCommentCount || 0;
  return comment.showReplies 
    ? `收起回复 (${replyCount})` 
    : `展开回复 (${replyCount})`;
};

// 处理切换回复显示状态
const toggleReplies = (comment: ExtendedComment) => {
  if (comment.showReplies === undefined) {
    // 如果还没有初始化，则添加一个showReplies属性
    comment.showReplies = true;
  } else {
    comment.showReplies = !comment.showReplies;
  }
};


// 从路由参数获取帖子ID并加载帖子详情
const loadPostDetail = async () => {
  try {
    const postId = getPostId();
    if (!postId || isNaN(postId) || postId <= 0) {
      console.error('Invalid post ID');
      return;
    }

    const response = await getPostDetail(postId);
    // 根据 API 响应处理数据 - API 返回的是 PostDetailResponse 类型
    if (response && response.code === 1 && response.data) {
      postDetail.value = response.data;
      
      currentPage.value = 1; // 重置到第一页
      // 加载第一页评论
      await loadComments(1, pageSize.value);
    } else {
      console.error('Failed to fetch post detail:', response);
    }
  } catch (error) {
    console.error('Error fetching post detail:', error);
  } finally {
    loading.value = false;
  }
};

// 加载评论
const loadComments = async (page: number, size: number) => {
  try {
    commentsLoading.value = true;
    
    const postId = getPostId();
    if (!postId || isNaN(postId) || postId <= 0) {
      console.error('Invalid post ID');
      return;
    }
    
    let response; // 定义response变量
    const params = {
      page: page, // 后端分页从1开始
      pageSize: size,
      postId: postId // 修正类型问题
    };

    console.log('Loading comments with params (attempt 1):', params);

    // 发送请求获取评论列表
    response = await getCommentList(params);
    
    console.log('Full Comment API Response (attempt 1):', response);
    
    // 根据API响应处理数据
    let records: Comment[] = []; // 修复：明确指定类型
    let total = 0;
    
    if (response && response.code === 1) {
      records = response.data?.records || [];
      total = response.data?.total || 0;
      
      // 调试：输出第一条评论的字段
      if (records.length > 0) {
        console.log('First comment fields:', Object.keys(records[0]));
        console.log('First comment data:', records[0]);
        records.forEach((comment, index) => {
          console.log(`Comment ${index} childCommentCount:`, comment.childCommentCount);
        });
      }
    }
    
    comments.value = records;
    totalComments.value = total;
    
    console.log(`Successfully loaded ${comments.value.length} comments out of total ${totalComments.value}`);
    console.log('First few comment data:', comments.value.slice(0, 2));
    
    // 检查评论是否有回复数字段
    if (comments.value.length > 0) {
      console.log('Checking first comment for childCommentCount:', comments.value[0].childCommentCount);
    }
  } catch (error) {
    console.error('请求评论列表时发生错误:', error);
    console.error('Error details:', error instanceof Error ? error.message : error);
    comments.value = [];
    totalComments.value = 0;
  } finally {
    commentsLoading.value = false;
  }
};

// 点赞/取消点赞
const toggleLike = async () => {
  if (!postDetail.value) return;
  
  try {
    // 如果用户未登录，提示登录
    if (!store.getters.isAuthenticated) {
      ElMessage.warning('请先登录');
      return;
    }

    // 调用API进行点赞/取消点赞
    const response = await likePost(postDetail.value.id);

    if (response && response.code === 1) {
      // 操作成功，重新加载数据以获取最新状态
      await loadPostDetail();
      ElMessage.success(postDetail.value.isLiked ? '已取消点赞' : '已点赞');
    } else {
      ElMessage.error(response.msg || '操作失败');
    }
  } catch (error: any) {
    console.error('点赞操作失败:', error);
    ElMessage.error('点赞操作失败，请稍后再试');
  }
};

// 收藏/取消收藏
const toggleFavorite = async () => {
  if (!postDetail.value) return;
  
  try {
    // 如果用户未登录，提示登录
    if (!store.getters.isAuthenticated) {
      ElMessage.warning('请先登录');
      return;
    }

    // 调用API进行收藏/取消收藏
    const response = await favoritePost(postDetail.value.id);

    if (response && response.code === 1) {
      // 操作成功，重新加载数据以获取最新状态
      await loadPostDetail();
      ElMessage.success(postDetail.value.isFavorited ? '已取消收藏' : '已收藏');
    } else {
      ElMessage.error(response.msg || '操作失败');
    }
  } catch (error: any) {
    console.error('收藏操作失败:', error);
    ElMessage.error('收藏操作失败，请稍后再试');
  }
};

// 关注/取消关注用户
const toggleFollowDetail = async (post: any) =>  {  // 使用 any 类型避免 ESLint 报错
  try {
    // 如果用户未登录，提示登录
    if (!store.getters.isAuthenticated) {
      ElMessage.warning('请先登录');
      return;
    }

    // 调用API关注/取消关注用户
    const response = await followUser(post.userId);
    if (response && response.code === 1) {
      // 更新帖子的关注状态
      if (postDetail.value) {
        postDetail.value.isFollowed = response.data.isFollowed;
      }
      // 提示关注成功或取消关注成功
      ElMessage.success(post.isFollowed ? '已取消关注' : '关注用户成功');
    } else {
      ElMessage.error(response.msg || '操作失败');
    }
  } catch (error: any) {
    console.error('关注操作失败:', error);
    ElMessage.error('关注操作失败，请稍后再试');
  }
};

// 举报帖子
const openReportDialog = () => {
  if (!postDetail.value) return;
  
  // 检查是否为自己发布的帖子，如果是则不允许举报
  if (computedUserId.value === postDetail.value.userId) {
    ElMessage.warning('不能举报自己的帖子');
    return;
  }
  
  // 调用子组件的打开对话框方法
  reportModalRef.value?.openReportDialog(1, postDetail.value.id);
};

// 举报评论
const openCommentReportDialog = (commentId: number) => {
  // 调用子组件的打开对话框方法
  reportModalRef.value?.openReportDialog(2, commentId); // 2 表示评论类型
};

// 处理举报事件（来自子评论）
const handleReportEvent = (payload: { targetType: number, targetId: number }) => {
  reportModalRef.value?.openReportDialog(payload.targetType, payload.targetId);
};

// 点赞/取消点赞评论
const toggleCommentLike = async (comment: Comment) => {
  try {
    // 如果用户未登录，提示登录
    if (!store.getters.isAuthenticated) {
      ElMessage.warning('请先登录');
      return;
    }

    // 乐观更新：先保存当前状态，用于失败时回滚
    const previousIsLiked = comment.isLiked;
    const previousLikeCount = comment.likeCount;
    
    // 立即更新UI状态（乐观更新）
    comment.isLiked = previousIsLiked === 1 ? 0 : 1;
    comment.likeCount = previousIsLiked === 1 ? previousLikeCount - 1 : previousLikeCount + 1;

    // 调用API进行点赞/取消点赞评论
    const response = await likeComment(comment.id);

    if (response && response.code === 1) {
      // API调用成功，保持乐观更新的状态
      ElMessage.success(comment.isLiked === 1 ? '已点赞' : '已取消点赞');
      
      // 同步更新帖子详情中的评论总数（如果存在）
      if (postDetail.value) {
        postDetail.value.commentCount = totalComments.value;
      }
    } else {
      // API调用失败，回滚UI状态
      comment.isLiked = previousIsLiked;
      comment.likeCount = previousLikeCount;
      ElMessage.error(response.msg || '操作失败');
    }
  } catch (error: any) {
    // 发生异常，回滚UI状态
    console.error('评论点赞操作失败:', error);
    ElMessage.error('评论点赞操作失败，请稍后再试');
    
    // 回滚到之前的状态
    comment.isLiked = comment.isLiked === 1 ? 0 : 1; // 反转回来
    // 由于可能有其他用户同时操作，重新加载数据以保证一致性
    await loadComments(currentPage.value, pageSize.value);
  }
};

// 删除评论
const deleteCurrentComment = async (comment: Comment) => {
  try {
    await ElMessageBox.confirm(
      '确定要删除这个评论吗？此操作不可撤销。',
      '删除确认',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }
    );
    
    // 调用删除评论的API
    const response = await deleteComment(comment.id);  // 使用导入的API函数
    
    if (response && response.code === 1) {
      ElMessage.success('评论删除成功');
      // 重新加载评论列表
      await loadComments(currentPage.value, pageSize.value);
      
      // 更新帖子的评论数
      if (postDetail.value) {
        postDetail.value.commentCount--;
      }
    } else {
      ElMessage.error(response.msg || '删除评论失败');
    }
  } catch (error) {
    // 用户取消删除或发生错误
    if (error !== 'cancel') {
      console.error('删除评论失败:', error);
      ElMessage.error('删除评论失败');
    }
  }
};

// 提交主评论
const submitPostComment = async () => {
  if (!mainCommentContent.value.trim()) {
    ElMessage.warning('请输入评论内容');
    return;
  }

  const postId = getPostId();
  if (!postId) {
    ElMessage.error('帖子ID不能为空');
    return;
  }

  submittingComment.value = true;
  
  // 显示审核中提示
  ElMessage.info('评论提交成功，正在审核中...');

  try {
    // 调用API创建评论
    const response = await createComment({
      postId: postId, // 这里应该传入数字类型
      parentId: undefined,  // 父评论ID，这里为undefined表示一级评论
      content: mainCommentContent.value.trim()
    });

    if (response && response.code === 1) {
      ElMessage.success(response.msg || '评论发表成功');
      
      // 重置评论框
      mainCommentContent.value = '';
      showCommentInput.value = false;
      
      // 重新加载评论列表
      await loadComments(currentPage.value, pageSize.value);
      
      // 更新帖子的评论数
      if (postDetail.value) {
        postDetail.value.commentCount++;
      }
      
    } else {
      ElMessage.error(response?.msg || '评论发表失败');
    }
  } catch (error) {
    console.error('提交评论失败:', error);
    ElMessage.error('提交评论失败，请稍后重试');
  } finally {
    submittingComment.value = false;
  }
};

// 取消主评论
const cancelMainComment = () => {
  showCommentInput.value = false;
  mainCommentContent.value = '';
};

// 显示回复输入框
const showReplyInput = (commentId: number) => {
  // 如果当前显示的就是这个评论的输入框，则隐藏它
  if (showingReplyInput.value === commentId) {
    showingReplyInput.value = null;
  } else {
    showingReplyInput.value = commentId;
    // 初始化回复内容为空字符串（如果尚未初始化）
    if (!(commentId in replyContents.value)) {
      replyContents.value[commentId] = '';
    }
  }
};

// 提交回复
const submitReply = async (commentId: number, parentName: string) => {
  const replyContent = replyContents.value[commentId];
  
  if (!replyContent || !replyContent.trim()) {
    ElMessage.warning('请输入回复内容');
    return;
  }

  const postId = getPostId();
  if (!postId) {
    ElMessage.error('帖子ID不能为空');
    return;
  }

  submittingReply.value = true;
  
  // 显示审核中提示
  ElMessage.info('回复提交成功，正在审核中...');

  try {
    // 调用API创建评论
    const response = await createComment({
      postId: postId,
      parentId: commentId,  // 父评论ID，这里是具体的评论ID
      content: replyContent.trim()
    });

    if (response && response.code === 1) {
      ElMessage.success('回复发表成功');
      
      // 清空该评论的回复内容并隐藏输入框
      replyContents.value[commentId] = '';
      showingReplyInput.value = null;
      
      // 重新加载评论列表
      await loadComments(currentPage.value, pageSize.value);
      
      // 更新帖子的评论数
      if (postDetail.value) {
        postDetail.value.commentCount++;
      }
      
    } else {
      ElMessage.error(response?.msg || '回复发表失败');
    }
  } catch (error: any) {
    console.error('提交回复失败:', error);
    ElMessage.error('提交回复失败，请稍后再试');
  } finally {
    submittingReply.value = false;
  }
};

// 取消回复
const cancelReply = () => {
  showingReplyInput.value = null;
};

// 监听路由参数变化，重新加载数据
const route = useRoute();
watch(
  () => route.params.postId,
  (newPostId) => {
    if (newPostId) {
      const postId = Number(newPostId);
      if (!isNaN(postId) && postId > 0) {
        console.log('Route param changed, reloading post detail for ID:', postId);
        // 重新加载帖子详情
        loading.value = true;
        postDetail.value = null;
        comments.value = []; // 清空评论
        loadPostDetail();
      }
    }
  }
);

// 监听 props 变化
watch(
  () => props?.postId,
  (newPostId) => {
    if (newPostId && newPostId > 0) {
      console.log('Prop changed, reloading post detail for ID:', newPostId);
      // 重新加载帖子详情
      loading.value = true;
      loadPostDetail();
    }
  }
);

onMounted(() => {
  console.log('PostDetailView mounted, attempting to load post detail');
  // 滚动到页面顶部
  window.scrollTo(0, 0);
  loadPostDetail();
});

</script>

<style scoped>
/* 导入手写风格字体 */
@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700&family=Quicksand:wght@500;600&family=Open+Sans:wght@400;600&display=swap');

/* 背景编织纹理 */
.post-detail-container::before {
  content: "";
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    radial-gradient(circle at 15px 15px, #d4a976 1px, transparent 2px),
    radial-gradient(circle at 30px 30px, #b8966b 1px, transparent 2px);
  background-size: 40px 40px;
  opacity: 0.15;
  z-index: -1;
  pointer-events: none;
}

.post-detail-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 25px;
  font-family: 'Open Sans', 'Nunito', 'Quicksand', sans-serif;
  background: #fffaf0;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(139, 109, 75, 0.15);
  border: 1px solid #e9dcc8;
  position: relative;
  min-height: 80vh;
}

.post-title {
  text-align: center;
  margin: 0 0 20px 0;
  padding-bottom: 15px;
  border-bottom: 1px solid #e9dcc8;
  color: #5a3e36;
  font-size: 28px;
  font-weight: 600;
}

.loading {
  text-align: center;
  padding: 40px;
  font-size: 18px;
  color: #8b6d4b;
  font-family: 'Quicksand', cursive;
}

.error {
  text-align: center;
  padding: 40px;
  color: #d4a976;
  font-size: 18px;
  font-family: 'Quicksand', cursive;
}

.post-header {
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding-bottom: 20px;
  border-bottom: 1px dashed #d4c19c;
  margin-bottom: 25px;
}

.user-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
}

.user-info-content {
  display: flex;
  align-items: center;
  gap: 15px;
  flex: 1;
}

.avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 3px solid #e9dcc8;
  object-fit: cover;
}

.user-text h3 {
  margin: 0;
  font-size: 18px;
  color: #5a3e36;
  font-family: 'Quicksand', cursive;
}

.create-time {
  margin: 0;
  color: #a8977a;
  font-size: 14px;
}

.user-text {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
}

.follow-btn {
  min-width: 60px;
  padding: 6px 12px;
  border: none;
  border-radius: 18px;
  background: #f8f3eb;
  color: #8b6d4b;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 5px rgba(139, 109, 75, 0.1);
  align-self: flex-start;
  margin-left: 10px;
  height: 28px;
}

.follow-btn:hover {
  background: #e9dcc8;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(139, 109, 75, 0.15);
}

.follow-btn.followed {
  background: #409eff;
  color: white;
}

.report-btn {
  min-width: 60px;
  padding: 6px 12px;
  border: none;
  border-radius: 18px;
  background: #ff6b6b;
  color: white;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 5px rgba(255, 107, 107, 0.3);
  align-self: flex-start;
  margin-left: 10px;
  height: 28px;
}

.report-btn:hover {
  background: #ff5252;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(255, 107, 107, 0.4);
}

/* 为按钮添加响应式样式 */
@media (max-width: 768px) {
  .follow-btn {
    min-width: 70px;
    padding: 6px 12px;
    font-size: 13px;
  }
  
  .report-btn {
    min-width: 70px;
    padding: 6px 12px;
    font-size: 13px;
  }
  
  .user-info {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .follow-btn {
    align-self: flex-end;
  }
  
  .report-btn {
    align-self: flex-end;
  }
}

.post-stats {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 5px;
}

.post-stats span {
  background: #f8f3eb;
  padding: 5px 12px;
  border-radius: 18px;
  font-size: 13px;
  color: #8b6d4b;
  display: flex;
  align-items: center;
  gap: 4px;
}

.post-content {
  margin-bottom: 25px;
}

.post-title {
  text-align: center;
  margin: 0 0 20px 0;
  padding-bottom: 15px;
  border-bottom: 1px solid #e9dcc8;
  color: #5a3e36;
  font-size: 28px;
  font-weight: 600;
}

.post-body {
  font-size: 16px;
  line-height: 1.8;
  color: #5a3e36;
  margin-bottom: 20px;
  white-space: pre-wrap; /* 保留换行符 */
}

/* 媒体列表样式 */
.media-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 15px 0;
}

/* 图片网格样式 */
.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;
}

.media-item {
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  aspect-ratio: 1/1; /* 图片统一设置为正方形比例 */
}

.image-wrapper, .video-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover; /* 保持比例填充整个容器 */
  cursor: pointer;
  transition: transform 0.3s ease;
}

.image-wrapper img:hover {
  transform: scale(1.05);
}

/* 视频独占一行样式 */
.video-full-width {
  width: 100%;
  display: flex;
  justify-content: center;
}

.video-wrapper {
  position: relative;
  width: 100%;
  max-width: 800px; /* 限制视频的最大宽度 */
  height: auto;
  aspect-ratio: 16/9; /* 视频保持16:9宽高比 */
  margin: 10px 0;
}

.video-wrapper video {
  width: 100%;
  height: 100%;
  object-fit: contain; /* 视频保持完整内容显示 */
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.video-duration {
  position: absolute;
  bottom: 5px;
  right: 5px;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
}

.cover-image {
  max-width: 100%;
  height: auto;
  display: block;
  margin: 15px 0;
  border-radius: 8px;
}

/* 图片预览模态框样式 - 使用深度选择器确保样式应用 */
:deep(.image-viewer-overlay) {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.95);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(5px);
}

:deep(.image-viewer-content) {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 120px; /* 为左右按钮预留空间 */
  box-sizing: border-box;
}

:deep(.image-display) {
  position: relative;
  max-width: 100%;
  max-height: 85vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.image-display img) {
  max-width: 100%;
  max-height: 85vh;
  object-fit: contain;
  border-radius: 4px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  display: block;
}

:deep(.image-counter) {
  position: absolute;
  bottom: -40px;
  left: 50%;
  transform: translateX(-50%);
  color: #fff;
  font-size: 16px;
  font-weight: 500;
  background: rgba(0, 0, 0, 0.6);
  padding: 5px 15px;
  border-radius: 20px;
  z-index: 10000;
}

:deep(.nav-btn) {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  font-size: 28px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
  transition: all 0.3s ease;
  z-index: 10000;
}

:deep(.nav-btn:hover) {
  background: rgba(255, 255, 255, 0.4);
  transform: translateY(-50%) scale(1.1);
}

:deep(.prev-btn) {
  left: 20px; /* 调整到内容区域外侧 */
}

:deep(.next-btn) {
  right: 20px; /* 调整到内容区域外侧 */
}

:deep(.close-btn) {
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  font-size: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  backdrop-filter: blur(4px);
  transition: all 0.3s ease;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.close-btn:hover) {
  background: rgba(255, 255, 255, 0.4);
  transform: scale(1.1);
}

.post-body {
  font-size: 16px;
  line-height: 1.8;
  color: #6b5b4d;
  padding: 20px;
  background: #fdfcf9;
  border-radius: 15px;
  border: 1px solid #f0e8da;
  white-space: pre-wrap;
}

.post-actions {
  display: flex;
  gap: 10px;
  margin: 20px 0;
  flex-wrap: wrap;
}

.action-btn {
  flex: 1;
  min-width: 80px;
  padding: 8px 12px;
  border: none;
  border-radius: 20px;
  background: #f8f3eb;
  color: #8b6d4b;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 5px rgba(139, 109, 75, 0.1);
  min-height: 32px;
}

.action-btn:hover {
  background: #e9dcc8;
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(139, 109, 75, 0.15);
}

.action-btn.liked {
  background: #ffebee;
  color: #e91e63;
}

.action-btn.favorited {
  background: #fff8e1;
  color: #ffc107;
}

.action-btn.comment-btn {
  background: #fff2e6;
  color: #d48341;
}

.action-btn.comment-btn:hover {
  background: #ffe0cc;
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(212, 131, 65, 0.15);
}

.user-actions {
  display: flex;
  gap: 8px;
  margin-left: auto;
}

.edit-btn {
  padding: 8px 12px;
  border: none;
  border-radius: 20px;
  background: #409EFF;
  color: white;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 5px rgba(64, 158, 255, 0.2);
  min-height: 32px;
}

.edit-btn:hover {
  background: #66b1ff;
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(64, 158, 255, 0.3);
}

.delete-btn {
  padding: 8px 12px;
  border: none;
  border-radius: 20px;
  background: #F56C6C;
  color: white;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 5px rgba(245, 108, 108, 0.2);
  min-height: 32px;
}

.delete-btn:hover {
  background: #f78989;
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(245, 108, 108, 0.3);
}

.hot-comment-section, .comments-section {
  margin-top: 30px;
}

.hot-comment-section h3, .comments-section h3 {
  font-size: 22px;
  color: #5a3e36;
  margin-bottom: 20px;
  font-family: 'Quicksand', cursive;
  padding-bottom: 10px;
  border-bottom: 2px dotted #d4c19c;
}

.comment-item {
  display: flex;
  margin-top: 20px;
  padding: 20px;
  background: #fffdf9;
  border-radius: 15px;
  border: 1px solid #f0e8da;
  box-shadow: 0 3px 8px rgba(139, 109, 75, 0.05);
}

.comment-item:first-child {
  margin-top: 0;
}

.comment-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  margin-right: 15px;
  border: 2px solid #e9dcc8;
  object-fit: cover;
}

.comment-content {
  flex: 1;
}

.comment-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  align-items: center;
}

.comment-header strong {
  color: #5a3e36;
  font-size: 16px;
}

.comment-time {
  color: #a8977a;
  font-size: 13px;
}

.comment-actions {
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: space-between;
  width: 100%;
}

.comment-actions > *:first-child {
  margin-right: auto;
}

.comment-like-btn,
.comment-reply-btn,
.show-replies-btn,
.comment-delete-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #8b6d4b;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 15px;
  transition: all 0.3s ease;
  min-height: 24px;
  min-width: 24px;
  justify-content: center;
}

.comment-like-btn:hover,
.comment-reply-btn:hover,
.show-replies-btn:hover,
.comment-delete-btn:hover {
  background-color: #f0e6d4;
  color: #d48341;
}

.comment-like-btn.liked {
  color: #e74c3c;
}

.comment-delete-btn {
  color: #e74c3c;
}

.comment-delete-btn:hover {
  background-color: #ffebee;
  color: #c0392b;
}

.comment-report-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #ff6b6b;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 15px;
  transition: all 0.3s ease;
  min-height: 24px;
  min-width: 24px;
  justify-content: center;
}

.comment-report-btn:hover {
  background-color: #ffebee;
  color: #ff5252;
}

.thumb-up-icon {
  font-size: 14px;
}

.comment-icon {
  margin-right: 6px;
  font-size: 14px;
}

.comment-like-btn {
  background: #f8f3eb;
  border: none;
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  color: #8b6d4b;
  font-size: 14px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(139, 109, 75, 0.1);
}

.comment-like-btn.liked {
  background: #ffebee;
  color: #e91e63;
}

.comment-like-btn:hover {
  background: #e9dcc8;
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(139, 109, 75, 0.15);
}

.comment-like-btn.liked:hover {
  background: #f8bbd0;
  color: #c2185b;
}

.comment-like-btn .thumb-up-icon {
  font-size: 18px;
  margin-right: 6px;
  vertical-align: middle;
}
  
/* 分页组件样式 */
.pagination-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 25px;
  padding: 15px 0;
}

/* 使用深度选择器确保样式应用到分页组件 */
.pagination-container :deep(.el-pagination) {
  --el-pagination-font-size: 14px;
  display: inline-flex;
  align-items: center;
}

.pagination-container :deep(.el-pager li) {
  background-color: #fffaf0;
  border: 1px solid #e9dcc8;
  color: #5a3e36;
  margin: 0 4px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.pagination-container :deep(.el-pager li:hover) {
  color: #d4a976;
  border-color: #d4a976;
}

.pagination-container :deep(.el-pager li.active) {
  background-color: #d4a976;
  color: white;
  border-color: #d4a976;
}

.pagination-container :deep(.el-pager li:not(.active):hover) {
  color: #d4a976;
}

.pagination-container :deep(.btn-prev),
.pagination-container :deep(.btn-next) {
  background-color: #fffaf0;
  border: 1px solid #e9dcc8;
  border-radius: 8px;
  color: #5a3e36;
  margin: 0 4px;
}

.pagination-container :deep(.btn-prev:hover),
.pagination-container :deep(.btn-next:hover) {
  color: #d4a976;
  border-color: #d4a976;
}

.pagination-container :deep(.el-pagination__sizes) {
  margin-right: 10px;
}

.pagination-container :deep(.el-select .el-input__wrapper) {
  border-radius: 8px;
  border: 1px solid #e9dcc8;
  box-shadow: 0 2px 4px rgba(139, 109, 75, 0.1);
}

.pagination-container :deep(.el-pagination__jump) {
  margin-left: 10px;
}

.pagination-container :deep(.el-pagination__total) {
  color: #8b6d4b;
}

.comment-like-btn:hover {
  background: #e9dcc8;
  transform: scale(1.05);
}
  
/* 展开更多评论下拉框样式 */
.more-comments-dropdown {
  margin-top: 15px;
  text-align: left;
}

.dropdown-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #f8f3eb;
  border: 1px solid #e9dcc8;
  border-radius: 18px;
  cursor: pointer;
  color: #8b6d4b;
  font-size: 13px;
  transition: all 0.3s ease;
}

.dropdown-toggle:hover {
  background: #e9dcc8;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(139, 109, 75, 0.15);
}

.dropdown-arrow {
  transition: transform 0.3s ease;
  font-size: 10px;
}

.dropdown-toggle:hover .dropdown-arrow {
  transform: rotate(180deg);
}

/* 为每个评论项添加下划线样式的回复按钮 */
.underline-reply-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
  margin: 10px 0;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 1px dashed #d4c19c;
  width: 100%;
  max-width: 200px;
}

.underline-reply-toggle .underline-text {
  margin-right: 6px;
  font-size: 13px;
  color: #8b6d4b;
  font-weight: 500;
  text-decoration: underline;
  text-decoration-color: #d4a976;
  text-decoration-thickness: 1.5px;
  text-underline-offset: 3px;
}

.underline-reply-toggle .arrow-icon {
  font-size: 11px;
  color: #8b6d4b;
  transition: transform 0.3s ease;
}

.underline-reply-toggle:hover {
  background-color: #f8f3eb;
  border-radius: 6px;
}

.underline-reply-toggle:hover .underline-text {
  color: #d4a976;
  text-decoration-color: #a88a5c;
}

.underline-reply-toggle:hover .arrow-icon {
  color: #d4a976;
}

/* 主评论输入框样式 */
.main-comment-input {
  margin-top: 20px;
  padding: 20px;
  background: #fffdf9;
  border-radius: 15px;
  border: 1px solid #f0e8da;
  box-shadow: 0 3px 8px rgba(139, 109, 75, 0.05);
  width: 100%;
  box-sizing: border-box;
}

/* 回复输入框样式 */
.reply-input-box {
  margin-top: 15px;
  margin-left: 20px;
  padding: 15px;
  background: #faf8f5;
  border-radius: 12px;
  border: 1px solid #e9dcc8;
  box-shadow: 0 2px 6px rgba(139, 109, 75, 0.05);
  width: 100%;
  box-sizing: border-box;
}

.comment-textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #e9dcc8;
  border-radius: 10px;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  background: #fffaf0;
  box-sizing: border-box;  /* 确保padding不会增加元素总宽度 */
  max-width: 100%;  /* 限制最大宽度 */
}

.comment-textarea:focus {
  outline: none;
  border-color: #d4a976;
  box-shadow: 0 0 0 2px rgba(212, 169, 118, 0.2);
}

.comment-actions-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 10px;
}

.cancel-btn, .submit-btn {
  padding: 8px 20px;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.cancel-btn {
  background: #f8f3eb;
  color: #8b6d4b;
}

.cancel-btn:hover {
  background: #e9dcc8;
}

.submit-btn {
  background: #d4a976;
  color: white;
}

.submit-btn:hover {
  background: #c09565;
}

.submit-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* 回复容器样式 */
.replies-container {
  margin-top: 15px;
  padding-left: 20px;
  border-left: 1px solid #eee;
}

/* 展开回复按钮样式 */
.show-replies-btn {
  background: #fff2e6;
  border: none;
  padding: 6px 14px;
  border-radius: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: #d48341;
  font-size: 13px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(212, 131, 65, 0.1);
  margin-right: 10px;
}

.show-replies-btn:hover {
  background: #ffe8d1;
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(212, 131, 65, 0.15);
}

@media (max-width: 768px) {
  .post-detail-container {
    padding: 15px;
    margin: 10px;
  }
  
  .post-title {
    font-size: 22px;
  }
  
  .post-stats {
    flex-direction: column;
    gap: 10px;
  }
  
  .post-actions {
    flex-direction: column;
  }
  
  .action-btn {
    min-width: auto;
  }
  
  .comment-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
  
  .comment-time {
    margin-top: 5px;
  }
}

/* 自定义图标 */
.icon-like::before {
  content: "👍";
  margin-right: 4px;
  font-size: 14px;
}

.icon-liked::before {
  content: "❤️";
  margin-right: 4px;
  font-size: 14px;
}

.icon-comment::before {
  content: "💬";
  margin-right: 4px;
  font-size: 14px;
}

.icon-favorite::before {
  content: "⭐";
  margin-right: 4px;
  font-size: 14px;
}

.icon-favorited::before {
  content: "🌟";
  margin-right: 4px;
  font-size: 14px;
}

.icon-follow::before {
  content: "➕";
  margin-right: 4px;
  font-size: 14px;
}

.icon-followed::before {
  content: "✔️";
  margin-right: 4px;
  font-size: 14px;
}

.icon-reply::before {
  content: "↩️";
  margin-right: 4px;
  font-size: 12px;
}

.icon-comment-like::before {
  content: "👍";
  font-size: 12px;
}

.icon-comment-liked::before {
  content: "❤️";
  font-size: 12px;
}

.icon-delete::before {
  content: "🗑️";
  font-size: 12px;
}

.post-stats span i {
  font-size: 14px;
  vertical-align: middle;
}
</style>